import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { useAuthStore } from '../../auth/store/useAuthStore';
import {
  clearAccountSetupCache,
  getRequiresOwnerOnboarding,
  refreshAccountSetup,
  resolveAccountSetupForSession,
} from '../session/accountSetupService';
import type { AccountSetupState, PendingEmployeeInvitation } from '../../auth/domain/accountSetup';

type RefreshAccountSetupOptions = {
  force?: boolean;
  /** Re-lee setup sin volver a `resolved: false` (evita flash de boot). */
  background?: boolean;
};

type AccountSetupContextValue = {
  accountSetup: AccountSetupState | null;
  requiresOwnerOnboarding: boolean | null;
  organizationClosedAt: string | null;
  pendingEmployeeInvitation: PendingEmployeeInvitation | null;
  awaitingEmployeeInvitation: boolean;
  resolved: boolean;
  refreshAccountSetup: (options?: RefreshAccountSetupOptions) => Promise<void>;
};

const AccountSetupContext = createContext<AccountSetupContextValue | null>(null);

export function AccountSetupProvider({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  const [accountSetup, setAccountSetup] = useState<AccountSetupState | null>(null);
  const [resolved, setResolved] = useState(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!session?.user?.id) {
      clearAccountSetupCache();
      setAccountSetup(null);
      setResolved(false);
      inFlightRef.current = null;
    }
  }, [session?.user?.id]);

  const load = useCallback(async (options?: RefreshAccountSetupOptions) => {
    const uid = useAuthStore.getState().session?.user?.id;
    if (!uid) {
      setAccountSetup(null);
      setResolved(true);
      return;
    }

    if (options?.force !== true && inFlightRef.current) {
      await inFlightRef.current;
      return;
    }

    const run = (async () => {
      try {
        if (options?.force === true) {
          const row = await refreshAccountSetup({ force: true });
          setAccountSetup(row);
          return;
        }
        // ponytail: no await SQLite antes del gate — resolve hace offline en paralelo
        const row = await resolveAccountSetupForSession(uid);
        setAccountSetup(row);
      } catch (e) {
        if (!isDatabaseUnavailableError(e)) {
          console.warn('[AccountSetupProvider] resolve', e);
        }
        const fallback = getRequiresOwnerOnboarding();
        if (fallback !== null) {
          setAccountSetup({
            requiresOwnerOnboarding: fallback,
            ownerSetupCompletedAt: null,
            organizationId: null,
            branchCount: 0,
            organizationClosedAt: null,
            pendingEmployeeInvitation: null,
            awaitingEmployeeInvitation: false,
          });
        }
      } finally {
        setResolved(true);
      }
    })();

    inFlightRef.current = run.finally(() => {
      inFlightRef.current = null;
    });
    await inFlightRef.current;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!session?.user?.id) return;
      setResolved(false);
      await load();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, load]);

  const refresh = useCallback(async (options?: RefreshAccountSetupOptions) => {
    if (options?.background !== true) {
      setResolved(false);
    }
    await load(options);
  }, [load]);

  const requiresOwnerOnboarding = accountSetup?.requiresOwnerOnboarding ?? getRequiresOwnerOnboarding();
  const organizationClosedAt = accountSetup?.organizationClosedAt ?? null;
  const pendingEmployeeInvitation = accountSetup?.pendingEmployeeInvitation ?? null;
  const awaitingEmployeeInvitation = accountSetup?.awaitingEmployeeInvitation === true;

  const value = useMemo(
    () => ({
      accountSetup,
      requiresOwnerOnboarding,
      organizationClosedAt,
      pendingEmployeeInvitation,
      awaitingEmployeeInvitation,
      resolved,
      refreshAccountSetup: refresh,
    }),
    [
      accountSetup,
      requiresOwnerOnboarding,
      organizationClosedAt,
      pendingEmployeeInvitation,
      awaitingEmployeeInvitation,
      resolved,
      refresh,
    ],
  );

  return <AccountSetupContext.Provider value={value}>{children}</AccountSetupContext.Provider>;
}

export function useAccountSetup(): AccountSetupContextValue {
  const ctx = useContext(AccountSetupContext);
  if (!ctx) {
    throw new Error('useAccountSetup debe usarse dentro de AccountSetupProvider');
  }
  return ctx;
}
